import React, { useState, useEffect, useCallback } from "react";
import { Grid, TextField, MenuItem, Typography } from "@mui/material";
import { useTrainingObjects } from "../../hooks/apiHooks/trainingObjects";
import { useCoursePackages } from "../../hooks/apiHooks/coursePackages";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import usePermissions from "../../redux/selectors/usePermissions";
import { courseTypesForAdmin } from "../../constants";
import SearchInput from "../../components/SearchInput/SearchInput";

const Filter = ({ onFilterChange, filters }) => {
  const t = useScopedTranslation("bulkAssignmentTool.filter");
  const { hasPolicyPartnerAdminAccess } = usePermissions();
  const availableCourseTypes = Object.values(courseTypesForAdmin).filter(
    type => hasPolicyPartnerAdminAccess || type !== "policy"
  );

  const includesCoursePackages = () =>
    coursePackages?.find(
      course_package => course_package.packageType === "course"
    );
  const includesManuals = () =>
    coursePackages?.find(
      course_package => course_package.packageType === "manual"
    );
  const packagesOrManuals = () => {
    if (includesCoursePackages() && includesManuals())
      return "packageAndManual";
    return includesManuals() ? "manual" : "package";
  };

  const {
    getTrainingObjects,
    isLoading: isLoadingTrainingObjects
  } = useTrainingObjects({
    fields: {
      trainingObject: ["name"]
    }
  });
  const {
    getCoursePackages,
    isLoading: isLoadingCoursePackages
  } = useCoursePackages();

  const [trainingObjects, setTrainingObjects] = useState([]);
  const [coursePackages, setCoursePackages] = useState([]);

  const { courseType, trainingObject, coursePackage } = filters || {};

  const refreshCoursePackageList = () => {
    getCoursePackages({
      sort: "name",
      fields: {
        coursePackage: ["name", "package_type"]
      },
      filters: {
        withAssignableTrainingObjectsForCustomer: true,
        assignedToCustomer: true
      }
    }).then(response => {
      return setCoursePackages(response.data);
    });
  };

  const refreshTrainingObjectList = () => {
    getTrainingObjects({
      filters: {
        assignedToCustomer: true,
        courseType: courseType,
        coursePackage: [coursePackage?.id]
      },
      sort: "name"
    }).then(response => setTrainingObjects(response.data));
  };

  useEffect(() => {
    refreshTrainingObjectList();
  }, [courseType, coursePackage]);

  useEffect(() => {
    refreshCoursePackageList();
  }, []);

  const setCourseType = useCallback(
    courseType => {
      onFilterChange({ ...filters, courseType });
    },
    [onFilterChange, filters]
  );

  const setTrainingObject = useCallback(
    trainingObject => {
      onFilterChange({ ...filters, trainingObject });
    },
    [onFilterChange, filters]
  );

  const setCoursePackage = useCallback(
    coursePackage => {
      onFilterChange({ ...filters, coursePackage });
    },
    [onFilterChange, filters]
  );

  const renderOption = (props, option) => (
    <li {...props}>
      <Typography noWrap sx={{ textOverflow: "ellipsis", overflow: "hidden" }}>
        {option.name}
      </Typography>
    </li>
  );

  const filterOptions = (options, { inputValue }) =>
    options.filter(
      ({ name }) => name.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
    );

  return (
    <Grid container spacing={2} sx={{ my: 1.5 }}>
      <Grid item md={4}>
        <SearchInput
          items={trainingObjects}
          renderOption={renderOption}
          getOptionLabel={({ name }) => name}
          filterOptions={filterOptions}
          itemSize={40}
          onChange={(e, value) => setTrainingObject(value)}
          getOptionSelected={(option, value) => option.id === value.id}
          placeholder={t("trainingObject.placeholder")}
          value={trainingObject || null}
          loading={isLoadingTrainingObjects}
        />
      </Grid>
      <Grid item md={4}>
        <TextField
          select
          fullWidth
          variant="filled"
          InputProps={{
            disableUnderline: true
          }}
          SelectProps={{
            displayEmpty: true
          }}
          value={courseType || ""}
          onChange={event => setCourseType(event.target.value)}
          hiddenLabel
        >
          <MenuItem value={""}>{t("courseType.emptyValue")}</MenuItem>
          {availableCourseTypes.map(type => (
            <MenuItem value={type} key={type}>
              {t("courseType.items." + type)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item md={4}>
        <SearchInput
          items={coursePackages}
          renderOption={renderOption}
          getOptionLabel={({ name }) => name}
          filterOptions={filterOptions}
          itemSize={40}
          onChange={(e, value) => setCoursePackage(value)}
          getOptionSelected={(option, value) => option.id === value.id}
          placeholder={t(`coursePackage.${packagesOrManuals()}`)}
          value={coursePackage || null}
          loading={isLoadingCoursePackages}
        />
      </Grid>
    </Grid>
  );
};

export default Filter;
